import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users,
  ShoppingCart,
  Truck,
  Package,
  CurrencyDollar,
  Drop,
  ArrowUp,
  ArrowDown,
  Clock
} from '@phosphor-icons/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, subtitle }) => (
  <Card className="hover:border-orange-500/50 transition-colors">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-heading font-bold mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
          <Icon weight="duotone" className="w-5 h-5 text-orange-500" />
        </div>
      </div>
      {trend && (
        <div className="flex items-center gap-1 mt-3 text-sm">
          {trend === 'up' ? (
            <ArrowUp className="w-4 h-4 text-emerald-500" />
          ) : (
            <ArrowDown className="w-4 h-4 text-red-500" />
          )}
          <span className={trend === 'up' ? 'text-emerald-500' : 'text-red-500'}>
            {trendValue}
          </span>
          <span className="text-muted-foreground">vs yesterday</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, activityRes, deliveriesRes, ordersRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentActivity(),
          dashboardAPI.getDeliveriesByDay(7),
          dashboardAPI.getOrdersSummary()
        ]);

        setStats(statsRes.data);
        setRecentActivity(activityRes.data);
        setDeliveriesData(deliveriesRes.data);
        setOrdersSummary(ordersRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-GB').format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's your operations overview.</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Customers"
          value={formatNumber(stats?.total_customers || 0)}
          icon={Users}
        />
        <StatCard
          title="Orders Today"
          value={formatNumber(stats?.total_orders_today || 0)}
          icon={ShoppingCart}
          subtitle={`${stats?.total_orders_pending || 0} pending`}
        />
        <StatCard
          title="Deliveries Today"
          value={`${stats?.total_deliveries_completed || 0}/${stats?.total_deliveries_today || 0}`}
          icon={Package}
          subtitle="Completed / Total"
        />
        <StatCard
          title="Active Loads"
          value={formatNumber(stats?.loads_in_progress || 0)}
          icon={Truck}
          subtitle={`${stats?.vehicles_active || 0} vehicles active`}
        />
      </div>

      {/* Revenue & Volume Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="Revenue Today"
          value={formatCurrency(stats?.total_revenue_today || 0)}
          icon={CurrencyDollar}
        />
        <StatCard
          title="Volume Delivered Today"
          value={`${formatNumber(stats?.total_volume_today || 0)} L`}
          icon={Drop}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deliveries Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Deliveries - Last 7 Days</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveriesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-GB', { weekday: 'short' })}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Bar dataKey="total" fill="hsl(var(--muted-foreground))" name="Total" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed" fill="hsl(var(--primary))" name="Completed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Orders Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Orders by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(ordersSummary).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge 
                      variant="outline" 
                      className={
                        status === 'delivered' ? 'status-success' :
                        status === 'pending' || status === 'confirmed' ? 'status-pending' :
                        status === 'cancelled' ? 'status-error' :
                        'status-info'
                      }
                    >
                      {status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <span className="font-mono font-bold tabular-nums">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No recent activity</p>
            ) : (
              recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-4 pb-4 border-b last:border-0 last:pb-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    {activity.type === 'order' ? (
                      <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Package className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{activity.description}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {new Date(activity.timestamp).toLocaleString('en-GB')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
